import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Order } from '../../../types/Order'
import styles from './FulfillmentList.module.css'
import cx from 'classnames'
import Card from '../../../components/Card'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFetchData } from '../../../hooks/useFetchData'
import { ApiRoutes } from '../../../services/ApiService'
import Pagination from '../../../components/Pagination'
import Loader from '../../../components/Loader'
import Input from '../../../components/Input'
import Select from '../../../components/Select'

const SORT_STORAGE_KEY = 'zr_fulfillment_sort'
const VALID_SORT_VALUES = ['newest', 'oldest'] as const

function getStoredSort(): string {
    const stored = localStorage.getItem(SORT_STORAGE_KEY)
    if (stored && VALID_SORT_VALUES.includes(stored as (typeof VALID_SORT_VALUES)[number])) {
        return stored
    }
    return 'newest'
}

const FulfillmentList: React.FC = () => {
    const { t } = useTranslation(['fulfillment'])
    const navigate = useNavigate()

    const [page, setPage] = useState<number>(1)
    const [searchValue, setSearchValue] = useState<string>('')
    const [debounceSearch, setDebounceSearch] = useState<string>('')
    const [sortValue, setSortValue] = useState<string>(getStoredSort)

    const sortOptions = useMemo(
        () => [
            { value: 'newest', label: t('overview.sort.newest') },
            { value: 'oldest', label: t('overview.sort.oldest') },
        ],
        [t]
    )

    const {
        data: orders,
        fetch,
        isLoading,
        pagination,
    } = useFetchData<Order>(ApiRoutes.ORDERS)

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebounceSearch(searchValue)
        }, 750)
        return () => clearTimeout(timer)
    }, [searchValue])

    useEffect(() => {
        fetch({
            page: page.toString(),
            perPage: '10',
            search: debounceSearch,
            sort: sortValue,
        })
    }, [fetch, page, debounceSearch, sortValue])

    return (
        <Fragment>
            <div
                className={'zr-d-flex zr-items-center zr-flex-row zr-gap-2 zr-mb-4 zr-w-50'}
            >
                <Input
                    type="text"
                    placeholder={t('overview.search')}
                    value={searchValue}
                    onChange={(_, value) => {
                        setSearchValue(value)
                        setPage(1)
                    }}
                    property={'search'}
                    classes={'zr-w-100'}
                />
                <Select
                    options={sortOptions}
                    onSelect={(_, value) => {
                        setSortValue(value)
                        setPage(1)
                        localStorage.setItem(SORT_STORAGE_KEY, value)
                    }}
                    property={'sort'}
                    value={sortValue}
                    placeholder={t('overview.sort.placeholder')}
                    classes={'zr-w-50'}
                    searchable={false}
                    clearable={false}
                />
            </div>
            {isLoading ? (
                <div className={'zr-w-100 zr-d-flex zr-justify-center'}>
                    <Loader isLoading={isLoading} />
                </div>
            ) : (
                <>
                    <div className={cx(styles['fulfillment-list'])}>
                        {orders.map((order) => (
                            <Card
                                key={order.id}
                                classes={cx(styles['fulfillment-item-card'])}
                            >
                                <div className={cx(styles['fulfillment-item'])}>
                                    <div className={'zr-flex zr-flex-col'}>
                                        <div
                                            className={cx(
                                                styles['fulfillment-item-header']
                                            )}
                                        >
                                            {t('list.no')}
                                        </div>
                                        <div>{order.number}</div>
                                    </div>
                                    <div className={'zr-flex zr-flex-col'}>
                                        <div
                                            className={cx(
                                                styles['fulfillment-item-header']
                                            )}
                                        >
                                            {t('list.customer')}
                                        </div>
                                        <div>
                                            {order.customer.firstname}{' '}
                                            {order.customer.lastname}
                                        </div>
                                    </div>
                                    <div className={'zr-flex zr-flex-col'}>
                                        <div
                                            className={cx(
                                                styles['fulfillment-item-header']
                                            )}
                                        >
                                            {t('list.date')}
                                        </div>
                                        <div>{order.datetime}</div>
                                    </div>
                                    <div className={'zr-flex zr-flex-col'}>
                                        <div
                                            className={cx(
                                                styles['fulfillment-item-header']
                                            )}
                                        >
                                            {t('list.status')}
                                        </div>
                                        <div>{order.status}</div>
                                    </div>
                                    <div className={'zr-flex zr-flex-col'}>
                                        <div
                                            className={cx(
                                                styles['fulfillment-item-header']
                                            )}
                                        >
                                            {t('list.count')}
                                        </div>
                                        <div>{order.customizations.length}</div>
                                    </div>
                                </div>
                                <div
                                    className={cx(
                                        styles['fulfillment-item-header'],
                                        'zr-mb-2'
                                    )}
                                >
                                    {t('list.customizations')}
                                </div>
                                <div className={'zr-flex zr-row zr-gap-2'}>
                                    <button
                                        className={cx(
                                            styles['fulfillment-customization']
                                        )}
                                        onClick={() =>
                                            navigate(
                                                `/fulfillment/orders/${order.number}`
                                            )
                                        }
                                    >
                                        {t('list.show_all')}
                                    </button>
                                    {order.customizations.map((customization) => (
                                        <button
                                            key={customization.id}
                                            className={cx(
                                                styles['fulfillment-customization']
                                            )}
                                            onClick={() => {
                                                navigate(
                                                    `/fulfillment/customizations/${customization.id}`
                                                )
                                            }}
                                        >
                                            #{customization.id}
                                        </button>
                                    ))}
                                </div>
                            </Card>
                        ))}
                    </div>
                    {pagination && pagination.lastPage > 1 && (
                        <Pagination
                            totalPages={pagination.lastPage}
                            currentPage={pagination.currentPage}
                            onPageChange={(p) => setPage(p)}
                        />
                    )}
                </>
            )}
        </Fragment>
    )
}

export default FulfillmentList
